import React from 'react';
import { Navigation, ThumbsDown, ThumbsUp } from 'lucide-react';
import { getExperienceCategory } from '../../utils/experienceCategories';
import { ExperienceCategoryGlyph } from './ExperienceCategoryGlyph';

const FONT = "'Inria Serif', serif";

/**
 * Static inline preview of how an experience will appear on the Guest Map
 * (`ExperiencePreviewSheet`). Mirrors that component's hero image box
 * (width:100%, height:160, objectFit:cover) + category chip + tags +
 * description + action row EXACTLY, so hosts can see photo cropping
 * problems BEFORE submitting (and swap the image if their photo gets
 * cropped unfavourably).
 */
export function HostExperienceGuestPreview({ category, description, photoPreviewUrl, tags = [] }) {
  const cat = category ? getExperienceCategory(category) : null;
  const safeTags = Array.isArray(tags) ? tags.slice(0, 3) : [];
  const chipColor = cat?.color || '#7C3AED';

  return (
    <div
      data-testid="host-add-experience-guest-preview"
      style={{
        background: 'var(--surface)',
        borderRadius: 22,
        boxShadow: '0 12px 32px rgba(15,17,21,0.14)',
        overflow: 'hidden',
        border: '1px solid var(--card-border)',
      }}
    >
      <div style={{ position: 'relative', width: '100%', height: 160, background: '#EAF0FF' }}>
        {photoPreviewUrl ? (
          <img
            src={photoPreviewUrl}
            alt=""
            data-testid="host-add-experience-guest-preview-image"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <div style={{
            width: '100%', height: '100%',
            background: `linear-gradient(135deg, ${chipColor}33, ${chipColor}11)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              width: 60, height: 60, borderRadius: '50%',
              background: chipColor,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <ExperienceCategoryGlyph glyph={cat?.glyph || 'target'} size={28} />
            </div>
          </div>
        )}
        <div style={{
          position: 'absolute', left: 12, bottom: 10,
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '5px 10px', borderRadius: 999,
          background: chipColor, color: '#FFFFFF',
          fontFamily: FONT, fontSize: 12, fontWeight: 700,
          boxShadow: '0 6px 14px rgba(8,12,24,0.30)',
        }}>
          <ExperienceCategoryGlyph glyph={cat?.glyph || 'target'} size={12} />
          {cat?.display || 'Experience'}
        </div>
      </div>

      {safeTags.length > 0 ? (
        <div style={{ padding: '12px 14px 0', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {safeTags.map((tag) => (
            <span key={tag} style={{
              padding: '4px 10px', borderRadius: 999,
              background: 'var(--surface-2, #F4F1FC)',
              color: 'var(--text)',
              fontFamily: FONT, fontSize: 12, fontWeight: 600,
              border: '1px solid var(--card-border)',
            }}>{tag}</span>
          ))}
        </div>
      ) : null}

      <div style={{
        padding: '10px 14px 12px',
        fontFamily: FONT, fontSize: 14, lineHeight: 1.45, color: 'var(--text)',
      }}>
        {description?.trim() || 'A hidden gem shared by a local host.'}
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 12px 12px',
        borderTop: '1px solid var(--card-border)',
        background: 'var(--surface)',
      }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', minWidth: 62, borderRadius: 999, border: '1.5px solid var(--card-border)', color: 'var(--text)', fontFamily: FONT, fontSize: 13, fontWeight: 700 }}>
          <ThumbsUp size={16} strokeWidth={2.2} /><span>0</span>
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', minWidth: 62, borderRadius: 999, border: '1.5px solid var(--card-border)', color: 'var(--text)', fontFamily: FONT, fontSize: 13, fontWeight: 700 }}>
          <ThumbsDown size={16} strokeWidth={2.2} />
        </div>
        <div style={{
          marginLeft: 'auto',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          padding: '9px 14px', borderRadius: 999,
          background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)',
          color: '#FFFFFF', fontFamily: FONT, fontSize: 13, fontWeight: 700,
          boxShadow: '0 8px 18px rgba(124, 58, 237, 0.32)', whiteSpace: 'nowrap',
        }}>
          <Navigation size={14} strokeWidth={2.4} />
          Get directions
        </div>
      </div>
    </div>
  );
}

export default HostExperienceGuestPreview;
